

/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Card } from '../../../bphComponents/bundle';
import HealthEntryModel, { HealthEntry } from '../../../models/HealthEntry';
import { AmountAndFrequency, Anatomy, DateSummary, Diagnosis, Reaction, StoppingReason, HealthEntryValueGeneVariant, HealthEntryValueGeneAttribute, HealthEntryValue } from './components/HEProperty.controller';
import { HealthEntryComments } from './components/OccurrenceComments';
import { HealthEntryContacts } from './components/OccurrenceContacts';
import { HealthEntryFiles } from './components/OccurrenceFiles';

import HETrackables from './components/HETrackables';
import { HECardTitle } from './components/CardTitle';
import IconAttachment from '../../components/icons/IconAttachment';
import BaseHEShareCards from './components/HEShareCards';
import { BackpackThemeMarkdown } from '../../components/Markdown';
import HealthEntryGeneAttributes from '../../../constants/HealthEntryGeneAttributes';
import COLORS from '../../styles/Colors';
import { useGeneVariantPresent, useMedliConstantsStoppingReasons } from '../../hooks/useMedli';

interface Props {
  healthEntry: HealthEntry;
  parentHealthEntryId?: number;
  profileId: number;
  languageCode: string;
  isNew: boolean;
}

const HEVDateSummary = (props) => (
  <DateSummary
    isChild={true}
    healthEntry={props.healthEntry}
    parentHealthEntryId={props.parentHealthEntryId}
    languageCode={props.languageCode}
  />
);

const HEDateSummary = (props) => (
  <DateSummary
    healthEntry={props.healthEntry}
    parentHealthEntryId={null}
    languageCode={props.languageCode}
  />
);
const HEComments = (props) => (
  <HealthEntryComments
    healthEntry={props.healthEntry}
    parentHealthEntryId={props.parentHealthEntryId}
  />
);

const HEContactAttachments = (props) => (
  <HealthEntryContacts
    healthEntry={props.healthEntry}
    parentHealthEntryId={props.parentHealthEntryId}
  />
);

const HEFiles = (props) => (
  <HealthEntryFiles
    healthEntry={props.healthEntry}
    parentHealthEntryId={props.parentHealthEntryId}
  />
);

const HEShareCards = (props) => (
  <BaseHEShareCards
    healthEntry={props.healthEntry}
    allowSettingIncludedValues={false}
    shareCardsDefaultExpanded={props.isNew}
  />
);

const HEAnatomy = (props) => (
  <Anatomy
    healthEntry={props.healthEntry}
    languageCode={props.languageCode}
  />
);

const HEAmountAndFrequency = (props) => (
  <AmountAndFrequency
    healthEntry={props.healthEntry}
    languageCode={props.languageCode}
  />
);

const HEStoppingReason = (props) => {
  return HealthEntryModel.hasStoppingReason(props.healthEntry.data()) || !HealthEntryModel.isCurrent(props.healthEntry.data())
    ? (
      <StoppingReason
        healthEntry={props.healthEntry}
        languageCode={props.languageCode}
      />
    ) : null;
};

const HEReaction = (props) => {
  const [, stopReasonActions] = useMedliConstantsStoppingReasons();

  const hasReaction = props.hasReaction || (
    HealthEntryModel.hasStoppingReason(props.healthEntry.data()) &&
    stopReasonActions.isReaction(props.healthEntry.stoppingReason.get())
  );

  return hasReaction
    ? (
      <Reaction
        healthEntry={props.healthEntry}
        languageCode={props.languageCode}
        isForAllergy={props.isForAllergy}
      />
    ) : null;
};

const HEDiagnosis = (props) => (
  <Diagnosis
    healthEntry={props.healthEntry}
    languageCode={props.languageCode}
  />
);

const HEValueGeneVariant = (props) => (
  <HealthEntryValueGeneVariant
    healthEntry={props.healthEntry}
    parentHealthEntryId={props.parentHealthEntryId}
    languageCode={props.languageCode}
  />
);

const hasGeneVariant = (props) => {
  const { healthEntry, getGeneVariantPresent } = props;
  const { geneVariantResult } = healthEntry;
  const variantPresentId = geneVariantResult.getVariantPresentId();
  const geneVariantPresent = getGeneVariantPresent(variantPresentId);

  return geneVariantPresent.hasVariant();
};

const HEValueGeneAttributes = (props) => {
  const [{ hasGeneVariant }] = useGeneVariantPresent(props.healthEntry.data());

  if (!hasGeneVariant) return null;

  const { healthEntry } = props;

  return (
    <Card.CardSection hasBottomBorder={true} style={{ backgroundColor: COLORS.csAppBGgray30 }}>
      {
        HealthEntryGeneAttributes.map((attribute, index) => (
          <HealthEntryValueGeneAttribute
            key={index}
            attributeScheme={attribute}
            healthEntry={healthEntry}
            languageCode={props.languageCode}
            parentHealthEntryId={props.parentHealthEntryId}
          />
        ))
      }
    </Card.CardSection>
  );
};

const HEGeneVariantHelpBlock = (props) => {
  const [{ hasGeneVariant }] = useGeneVariantPresent(props.healthEntry.data());

  if (!hasGeneVariant) return null;

  return (
    <Card.CardShape
      isFlat={true}
      className="bkgd-color-gsLight"
    >
      <Card.CardContent>
        <BackpackThemeMarkdown phraseId="healthentry.gene.helpBlock.text" />
      </Card.CardContent>
    </Card.CardShape>
  );
};

const HEValue = (props) => (
  <HealthEntryValue
    healthEntry={props.healthEntry}
    parentHealthEntryId={props.parentHealthEntryId}
    languageCode={props.languageCode}
  />
);

// ===============

export const Allergy: React.FC<Props> = (props) => (
  <div>
    <Card.CardShape>
      <HEDateSummary {...props} />
      <HEDiagnosis {...props} />
      <HEReaction {...props} hasReaction={true} isForAllergy={true} />
      <HEComments {...props} />
      <HEContactAttachments {...props} />
    </Card.CardShape>

    <HEFiles {...props} />
    <HEShareCards {...props} healthEntry={props.healthEntry.data()} />
  </div>
);

export const Condition: React.FC<Props> = (props) => (
  <div>
    <Card.CardShape>
      <HEDateSummary {...props} />
      <HEDiagnosis {...props} />
      <HEAnatomy {...props} />
      <HEComments {...props} />
      <HEContactAttachments {...props} />
    </Card.CardShape>

    <HETrackables {...props} healthEntry={props.healthEntry.data()} />
    <HEFiles {...props} />
    <HEShareCards {...props} healthEntry={props.healthEntry.data()} />
  </div>
);

export const GeneVariantResult: React.FC<Props> = (props) => (
  <div>
    <Card.CardShape>
      <HEVDateSummary {...props} />
      <HEValueGeneVariant {...props} />
      <HEValueGeneAttributes {...props} />
      <HEComments {...props} />
      <HEContactAttachments {...props} />
    </Card.CardShape>

    <HEFiles {...props} />
    <HEGeneVariantHelpBlock {...props} />
  </div>
);

export const Lab: React.FC<Props> = (props) => (
  <div>
    <Card.CardShape>
      <HEVDateSummary {...props} />
      <HEValue {...props} />
      <HEComments {...props} />
      <HEContactAttachments {...props} />
    </Card.CardShape>

    <HEFiles {...props} />
  </div>
);

export const Measurement: React.FC<Props> = (props) => (
  <div>
    <Card.CardShape>
      <HEVDateSummary {...props} />
      <HEValue {...props} />
      <HEComments {...props} />
      <HEContactAttachments {...props} />
    </Card.CardShape>

    <HEFiles {...props} />
  </div>
);

export const Medication: React.FC<Props> = (props) => (
  <div>
    <Card.CardShape>
      <HEDateSummary {...props} />
      <HEAmountAndFrequency {...props} />
      <HEStoppingReason {...props} />
      <HEReaction {...props} isForAllergy={false} />
      <HEComments {...props} />
      <HEContactAttachments {...props} />
    </Card.CardShape>

    <HEFiles {...props} />
    <HEShareCards {...props} healthEntry={props.healthEntry.data()} />
  </div>
);

export const OtherTreatment: React.FC<Props> = (props) => (
  <div>
    <Card.CardShape>
      <HEDateSummary {...props} />
      <HEAmountAndFrequency {...props} />
      <HEStoppingReason {...props} />
      <HEReaction {...props} isForAllergy={false} />
      <HEAnatomy {...props} />
      <HEComments {...props} />
      <HEContactAttachments {...props} />
    </Card.CardShape>

    <HEFiles {...props} />
    <HEShareCards {...props} healthEntry={props.healthEntry.data()} />
  </div>
);

export const Procedure: React.FC<Props> = (props) => (
  <div>
    <Card.CardShape>
      <HEDateSummary {...props} />
      <HEAnatomy {...props} />
      <HEComments {...props} />
      <HEContactAttachments {...props} />
    </Card.CardShape>

    <HEFiles {...props} />
    <HEShareCards {...props} healthEntry={props.healthEntry.data()} />
  </div>
);

export const Vaccination: React.FC<Props> = (props) => (
  <div>
    <Card.CardShape>
      <HEDateSummary {...props} />
      <HEComments {...props} />
      <HEContactAttachments {...props} />
    </Card.CardShape>

    <HEFiles {...props} />
    <HEShareCards {...props} healthEntry={props.healthEntry.data()} />
  </div>
);
